import { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Link, useLoaderData, useRouteLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import FullScreenVideo from "~/components/common/FullScreenVideo";
import Waves from "~/components/icons/Waves";
import AutoplayCarousel from "~/components/ui/AutoplayCarousel";
import { Button } from "~/components/ui/Button";
import {
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from "~/components/ui/Carousel";
import { getLocale } from "~/lib/i18n.server";
import { cn, getMenuHref, getMetaData, isEnabled } from "~/lib/utils";

export const meta: MetaFunction = (data: any) => {
  const { t } = useTranslation();
  return getMetaData(data, {
    title: t("Mondi"),
    description: t(
      "Scopri i mondi di benessere di ChorusLife. Percorsi unici e rituali sensoriali pensati per il tuo completo relax e la rigenerazione di corpo e mente.",
    ),
  });
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "mondi";

    const locale = await getLocale(request);
    const lang = locale;
    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`,
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }
    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
    };
  }
}

export default function Worlds() {
  const loaderData = useLoaderData<typeof loader>();
  console.log('loaderData', loaderData)
  const { t, i18n } = useTranslation();
  const { bookingDisabled, shopDisabled } = useRouteLoaderData<any>("root");

  return (
    <div
      className={`
        relative
      `}
    >
      <main className="flex flex-col lg:pb-12">
        {loaderData.page.acf?.hero && (
          <section className="bg-secondary">
            <div className="py-8 px-4">
              <div className="flex flex-col max-w-4xl mx-auto items-center justify-center">
                <h1 className="text-4xl lg:text-5xl text-white font-tenor-sans uppercase">
                  {loaderData.page.acf?.hero.title}
                </h1>
                <hr className="block w-16 h-[2px] bg-white mt-2" />
                <p className="text-white text-lg mt-4 text-center">
                  {loaderData.page.acf?.hero.description}
                </p>
              </div>
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.info_section && (
          <section className="w-full">
            <div className="flex flex-col justify-center text-center max-w-7xl mx-auto py-12 px-4">
              {loaderData.page.acf?.info_section?.info_section_video?.id && (
                <div className="mb-8 rounded-3xl overflow-hidden shadow-2xl max-w-4xl mx-auto w-full">
                  <FullScreenVideo
                    autoplay
                    videoPreview={`/cms-asset/${loaderData.page.acf?.info_section?.info_section_video?.id}`}
                    videoSrc={`/cms-asset/${loaderData.page.acf?.info_section?.info_section_video?.id}`}
                  />
                </div>
              )}
              <div className="flex flex-col items-center">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                  {loaderData.page.acf?.info_section.title}
                </h4>
                <hr className="block w-44 h-[2px] bg-secondary mt-2" />

                <div
                  className="mt-2"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.info_section.description,
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {loaderData.page?.acf?.rituals && (
          <section className="w-full py-12">
            <div className="max-w-7xl mx-auto w-full">
              <div className="grid grid-cols-1 gap-6 mt-8">
                {loaderData.page?.acf?.rituals?.length > 0 &&
                  loaderData.page?.acf?.rituals?.map(
                    (ritual: any, index: number) => (
                      <div key={index} className="flex flex-col px-4 pb-0">
                        <div className="flex flex-col lg:flex-row h-full justify-center w-full pb-8 lg:pb-4">
                          <div className="flex flex-col h-full justify-center pb-8 pr-6 flex-1">
                            <div>
                              <h4 className="text-secondary text-3xl font-tenor-sans lg:mt-6 uppercase">
                                {ritual.title}
                              </h4>
                              <div
                                className="text-md mt-2"
                                dangerouslySetInnerHTML={{
                                  __html: ritual.description,
                                }}
                              ></div>
                            </div>
                            {/* <div>
                          <h4 className="text-secondary text-xl font-tenor-sans mt-8">
                            {ritual.second_title}
                          </h4>
                          <hr className="block w-12 h-[2px] bg-secondary mt-2" />
                          <div
                            className="text-sm mt-2 h5-black"
                            dangerouslySetInnerHTML={{
                              __html: ritual.second_description,
                            }}
                          ></div>
                        </div> */}
                          </div>
                          <div className="lg:pl-6 w-full lg:w-[40rem]">
                            <AutoplayCarousel
                              opts={{ loop: true }}
                              className="w-full"
                            >
                              <CarouselContent>
                                {ritual.gallery?.map(
                                  (_: any, index: number) => (
                                    <CarouselItem key={index}>
                                      <div className="w-full h-72 lg:h-96">
                                        <span className="text-4xl font-semibold">
                                          <img
                                            alt=""
                                            src={`/cms-asset/${_?.id}`}
                                            className="w-full h-full object-cover object-center"
                                          />
                                        </span>
                                      </div>
                                    </CarouselItem>
                                  ),
                                )}
                              </CarouselContent>
                              <CarouselDots className="relative top-0 bottom-0 mt-4 justify-end" />
                            </AutoplayCarousel>
                          </div>
                        </div>
                        <hr className="block w-full h-[1px] bg-secondary mb-4" />
                      </div>
                    ),
                  )}
              </div>
            </div>
          </section>
        )}

        {loaderData.page.acf?.second_section_rituals &&
          isEnabled(
            loaderData.page.acf?.second_section_rituals
              ?.second_section_rituals_enable,
          ) && (
            <section className="w-full bg-muted relative overflow-hidden py-20">
              <div className="absolute w-full h-full top-0 left-0 z-0">
                <div className="max-w-5xl mx-auto flex justify-end">
                  <Waves />
                </div>
              </div>
              <div className="flex flex-col justify-center text-center max-w-5xl mx-auto pb-0 relative z-10 px-4">
                <div className="flex flex-col items-center">
                  <h4 className="text-secondary max-w-2xl font-tenor-sans text-2xl lg:text-4xl uppercase">
                    {loaderData.page.acf?.second_section_rituals.title}
                  </h4>
                  <hr className="block w-44 h-[1px] bg-secondary border-none mt-2" />

                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html:
                        loaderData.page.acf?.second_section_rituals.description,
                    }}
                  />
                </div>
              </div>
              <div className="max-w-7xl mx-auto w-full  relative z-10">
                <div className="flex flex-row flex-wrap justify-center mt-8 lg:space-x-4">
                  {loaderData.page.acf?.second_section_rituals.rituals.map(
                    (ritual: any, index: number) => (
                      <div key={index} className="flex flex-row w-[21rem] mt-4">
                        <div
                          className={cn(
                            "flex flex-col h-full w-full items-center justify-center g-cover bg-center bg-no-repeat pt-28 px-4 pb-4 rounded-2xl",
                            {
                              "text-secondary":
                                ritual.subtitle_variant === "secondary",
                              "text-white": ritual.subtitle_variant === "white",
                            },
                          )}
                          style={{
                            backgroundImage: `url('/cms-asset/${ritual.image?.id}')`,
                          }}
                        >
                          <h4 className=" text-2xl font-tenor-sans mt-6 uppercase text-center">
                            {ritual.title}
                          </h4>
                          <hr
                            className={cn(
                              "block w-12 h-[1px] mt-2 border-none",
                              {
                                "bg-secondary":
                                  ritual.subtitle_variant === "secondary",
                                "bg-white": ritual.subtitle_variant === "white",
                              },
                            )}
                          />
                          <h6 className=" mt-2 text-center">
                            {ritual.subtitle}
                          </h6>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </section>
          )}

        {loaderData.page.acf?.booking_section && (
          <section className="max-w-7xl mx-auto w-full lg:mt-12">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${loaderData.page.acf?.booking_section.image?.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-20 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                  <h1 className="text-2xl lg:text-4xl text-center font-tenor-sans text-white">
                    {loaderData.page.acf?.booking_section.title}
                  </h1>
                  <p className="text-white mt-4 text-xl text-center">
                    {loaderData.page.acf?.booking_section.description}
                  </p>
                  <div className="flex items-center justify-center space-x-2">
                    <Link
                      to={
                        bookingDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/booking")
                      }
                    >
                      <Button variant={"muted"} className="mt-4">
                        {t("Prenota")}
                      </Button>
                    </Link>
                    <Link
                      to={
                        shopDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/shop")
                      }
                    >
                      <Button variant={"muted"} className="mt-4">
                        {t("Regala")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
